/**
 * Agent Discovery Hook
 *
 * Fetches available agent configurations from the AgentsViewSet API and filters them
 * based on current page context to only show agents whose context requirements are satisfied.
 * Context requirements are stored in the agent_metadata.context_requirements field.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAIShellContext } from '../AIShellContext';
import type { PageContext } from '../services/ContextDetectionService';
import type { ContextRequirements } from '../types';

/**
 * Check if context requirements are satisfied
 * @param requirements - The agent's context requirements
 * @param context - The current page context
 * @returns true if all requirements are met
 */
export function isContextRequirementsMet(requirements: ContextRequirements, context: PageContext): boolean {
  const { required, required_any: requiredAny } = requirements;

  // Check all required fields are present
  const allRequiredMet = !required?.length || required.every((field) => !!context[field]);

  // Check at least one required_any field is present (if specified)
  const anyRequiredMet = !requiredAny?.length || requiredAny.some((field) => !!context[field]);

  return allRequiredMet && anyRequiredMet;
}

export interface AgentDefinition {
  uuid: string;
  name: string;
  kit_name: string; // Added for consistent identification
  title: string;
  description: string;
  specialization?: string;
  context_requirements: ContextRequirements;
  is_global: boolean;
  organization?: string;
  created_at: string;
  updated_at: string;
}

interface AgentConfigurationResponse {
  uuid: string;
  title: string;
  name: string;
  description: string;
  specialization?: string;
  is_global: boolean;
  organization?: string;
  created_at: string;
  updated_at: string;
  agent_metadata: {
    context_requirements: ContextRequirements;
    kit_name?: string;
    [key: string]: unknown;
  };
}

interface AgentListResponse {
  total_count: number;
  data: AgentConfigurationResponse[];
  next?: string;
  previous?: string;
}

export interface UseAgentDiscoveryResult {
  availableAgents: AgentDefinition[];
  loading: boolean;
  error: string | null;
  refreshAgents: () => void;
}

export const useAgentDiscovery = (): UseAgentDiscoveryResult => {
  const { pageDetectedContext } = useAIShellContext();
  const [allAgents, setAllAgents] = useState<AgentDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/assistant/agents/', {
        method: 'GET',
        credentials: 'include', // Include session cookies
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.status} ${response.statusText}`);
      }

      const data: AgentListResponse = await response.json();

      // Transform the agent configuration data to our expected format
      const transformedAgents: AgentDefinition[] = data.data.map((agent) => {
        const kitName = agent.agent_metadata?.kit_name || agent.name;

        return {
          uuid: agent.uuid,
          name: agent.name,
          kit_name: kitName,
          title: agent.title,
          description: agent.description,
          specialization: agent.specialization,
          context_requirements: agent.agent_metadata?.context_requirements || {
            type: 'global',
            required: [],
          },
          is_global: agent.is_global,
          organization: agent.organization,
          created_at: agent.created_at,
          updated_at: agent.updated_at,
        };
      });

      setAllAgents(transformedAgents);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching agents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAgents = useCallback(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Filter agents based on context availability
  // Re-filters automatically when allAgents or pageDetectedContext changes
  const availableAgents = useMemo(() => {
    return allAgents.filter((agent) => {
      const { type } = agent.context_requirements;

      // Global agents are always available
      if (type === 'global') {
        return true;
      }

      // Context-specific agents require their context requirements to be met
      // This includes both 'required' (all must be present) and 'required_any' (at least one must be present)
      if (type === 'context_specific') {
        return isContextRequirementsMet(agent.context_requirements, pageDetectedContext);
      }

      // Unknown type, default to not available
      return false;
    });
  }, [allAgents, pageDetectedContext]);

  // Initial fetch on mount
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return {
    availableAgents,
    loading,
    error,
    refreshAgents,
  };
};

export default useAgentDiscovery;
